import React, { useState, useEffect, useRef } from 'react';
import {
  getSOVLines,
  getTaskAllocations,
  getSOVAllocations,
  getSOVAllocatedTotals,
  createAllocation,
  deleteAllocation,
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';

/**
 * SOV links section embedded inside the task Properties modal
 * (TaskEditModal). Was previously a standalone modal triggered by a
 * link icon on each task row, but that icon was removed when the
 * Tasks + Gantt tabs got merged into the Schedule view, leaving the
 * SOVLinkModal orphaned. Lifted into a reusable section component
 * here so the linking UX lives where users now expect it: in the
 * Properties dialog for the task itself.
 *
 * 2026-05-27: warm-cream + sage token redesign from the Claude Design
 * handoff `~/Downloads/design_handoff_task_properties`. Behavior
 * preserved verbatim — collapsed-by-default chip, expand-to-pick
 * panel, search-as-you-type combobox over SOV lines with
 * over-allocation guard (warning ring + inset-stripe banner + disabled
 * Add). Add still hits the API immediately; remove still 2-steps
 * through ConfirmDialog. Single-add UX: panel collapses after success
 * (vs. predecessors which stay open).
 *
 * Props:
 *   task                       Task object (only .id and .task_name used)
 *   projectId                  Project id (for fetching SOV lines + creating allocations)
 *   onAllocationsChanged       Optional. Called after every add/remove
 *                              so the parent can refresh dependent state.
 */

/* ─────────────────────── Inline-SVG icons ─────────────────────── */

const TrashIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);
const PlusIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const SearchIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
  </svg>
);
const AlertTriangleIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" />
  </svg>
);
const Spinner = ({ size = 13, color = '#fff' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: 99,
    border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color,
    animation: 'spin .8s linear infinite',
    flexShrink: 0,
  }} />
);

const TaskSOVLinks = ({ task, projectId, onAllocationsChanged, mode, localLinks, onLocalChange }) => {
  // "local" mode powers the Add Task modal: an allocation needs a saved
  // task_id, so links are collected in the parent's state here and committed
  // after the task is created. Server mode (default, in Task Properties) hits
  // the allocations API immediately, exactly as before.
  const isLocal = mode === 'local';
  const { confirm, confirmDialogProps } = useConfirm();

  const [allocations, setAllocations] = useState([]);
  const [sovLines, setSovLines] = useState([]);
  const [sovAllocatedMap, setSovAllocatedMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [addingLink, setAddingLink] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // Picker open/closed
  const [showPicker, setShowPicker] = useState(false);

  // Form state
  const [sovLineId, setSovLineId] = useState('');
  const [allocationType, setAllocationType] = useState('Percentage');
  const [allocationValue, setAllocationValue] = useState('100');
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);

  // Initial load: allocations on this task + all SOV lines on the
  // project + how much of each SOV line is already allocated elsewhere.
  // 3 parallel requests total — was 2 + N before /allocated-totals.
  useEffect(() => {
    if (!projectId) return;
    if (!isLocal && !task?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Local mode has no saved task yet, so there are no task allocations to
        // load — just the project's SOV lines + how much of each is already
        // allocated elsewhere (for the over-allocation guard).
        const [allocs, lines, totals] = await Promise.all([
          isLocal ? Promise.resolve([]) : getTaskAllocations(task.id),
          getSOVLines(projectId),
          getSOVAllocatedTotals(projectId),
        ]);
        if (cancelled) return;
        if (!isLocal) setAllocations(allocs);
        setSovLines(lines);
        setSovAllocatedMap(totals || {});
      } catch (e) {
        console.error('Error loading SOV link data:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [task?.id, projectId, isLocal]);

  // Refresh a single line's allocated total after add/remove.
  const refreshAllocated = async (lineId) => {
    try {
      const allocs = await getSOVAllocations(lineId);
      const totalAllocated = allocs.reduce(
        (sum, a) => sum + (parseFloat(a.allocation_amount) || 0),
        0,
      );
      setSovAllocatedMap(prev => ({ ...prev, [lineId]: totalAllocated }));
    } catch (_e) { /* ignore */ }
  };

  const filteredSovLines = sovLines.filter(line => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (line.sov_code || '').toLowerCase().includes(q) ||
           (line.description || '').toLowerCase().includes(q) ||
           (line.building_area_phase || '').toLowerCase().includes(q);
  });

  const selectedSovLine = sovLineId ? sovLines.find(l => l.id === parseInt(sovLineId, 10)) : null;
  const selectedContractAmt = selectedSovLine ? (parseFloat(selectedSovLine.contract_amount) || 0) : 0;
  const selectedTotalAllocated = selectedSovLine ? (sovAllocatedMap[selectedSovLine.id] || 0) : 0;
  const selectedRemainingAmt = selectedContractAmt - selectedTotalAllocated;
  const selectedRemainingPct = selectedContractAmt > 0
    ? (selectedRemainingAmt / selectedContractAmt) * 100
    : 0;

  // Would the user's current input exceed the remaining capacity?
  const requestedPercent = (() => {
    if (!sovLineId || !allocationValue) return 0;
    if (allocationType === 'Percentage') return parseFloat(allocationValue) || 0;
    if (selectedContractAmt > 0) {
      return ((parseFloat(allocationValue) || 0) / selectedContractAmt) * 100;
    }
    return 0;
  })();
  const wouldExceed = sovLineId && allocationValue &&
    requestedPercent > Math.round(selectedRemainingPct * 100) / 100 + 0.01;
  const exceedDollars = selectedRemainingAmt > 0 && wouldExceed
    ? ((requestedPercent - selectedRemainingPct) / 100) * selectedContractAmt
    : 0;
  const exceedPct = wouldExceed ? Math.max(0, requestedPercent - selectedRemainingPct) : 0;

  // In local mode the "linked" rows come from the parent's pending list; in
  // server mode they're the persisted allocations.
  const displayLinks = isLocal ? (localLinks || []) : allocations;

  const resetForm = () => {
    setSovLineId('');
    setAllocationValue(allocationType === 'Percentage' ? '100' : '');
    setSearchQuery('');
  };

  const handleAddLink = async () => {
    if (addingLink) return;
    if (!sovLineId || !allocationValue) return;
    const selectedLine = sovLines.find(l => l.id === parseInt(sovLineId, 10));
    if (!selectedLine) return;

    let amount = 0;
    let percent = null;
    if (allocationType === 'Percentage') {
      percent = parseFloat(allocationValue);
      amount = (selectedLine.contract_amount * percent) / 100;
    } else {
      amount = parseFloat(allocationValue);
      percent = selectedLine.contract_amount > 0
        ? (amount / selectedLine.contract_amount) * 100
        : 0;
    }

    const contractAmt = parseFloat(selectedLine.contract_amount) || 0;
    const totalAllocated = sovAllocatedMap[selectedLine.id] || 0;
    const remainingAmt = contractAmt - totalAllocated;
    if (amount > remainingAmt + 0.01) {
      // The inline warning row + disabled Add already cover this case,
      // but keep the hard cap so server-side mismatches surface clearly.
      return;
    }

    // Local mode: stash the pick in the parent's pending list (committed after
    // the task is created) and reserve its capacity so further picks + the
    // over-allocation guard account for it. No API call.
    if (isLocal) {
      const localId = `local-${selectedLine.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const link = {
        id: localId,
        sov_line_id: parseInt(sovLineId, 10),
        allocation_method: allocationType,
        allocation_percent: percent,
        allocation_amount: amount,
        sov_code: selectedLine.sov_code,
        sov_line_description: selectedLine.description,
        sov_contract_amount: selectedLine.contract_amount,
      };
      if (onLocalChange) onLocalChange([...(localLinks || []), link]);
      setSovAllocatedMap(prev => ({ ...prev, [selectedLine.id]: (prev[selectedLine.id] || 0) + amount }));
      resetForm();
      setShowPicker(false);
      return;
    }

    try {
      setAddingLink(true);
      await createAllocation({
        project_id: projectId,
        task_id: task.id,
        sov_line_id: parseInt(sovLineId, 10),
        allocation_method: allocationType,
        allocation_percent: percent,
        allocation_amount: amount,
      });
      const updated = await getTaskAllocations(task.id);
      setAllocations(updated);
      await refreshAllocated(parseInt(sovLineId, 10));
      // Single-add UX: collapse the picker after success.
      resetForm();
      setShowPicker(false);
      if (onAllocationsChanged) onAllocationsChanged();
    } catch (error) {
      console.error('Error adding link:', error);
    } finally {
      setAddingLink(false);
    }
  };

  const handleRemoveLink = async (alloc) => {
    // Local mode: just drop the pending pick and give its capacity back.
    if (isLocal) {
      if (onLocalChange) onLocalChange((localLinks || []).filter(l => l.id !== alloc.id));
      setSovAllocatedMap(prev => {
        const amt = parseFloat(alloc.allocation_amount) || 0;
        const cur = prev[alloc.sov_line_id] || 0;
        return { ...prev, [alloc.sov_line_id]: Math.max(0, cur - amt) };
      });
      return;
    }
    if (removingId) return;
    const ok = await confirm({
      title: 'Remove SOV Link',
      message: 'Are you sure you want to remove this SOV link?',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      setRemovingId(alloc.id);
      await deleteAllocation(alloc.id);
      const updated = await getTaskAllocations(task.id);
      setAllocations(updated);
      await refreshAllocated(alloc.sov_line_id);
      if (onAllocationsChanged) onAllocationsChanged();
    } catch (error) {
      console.error('Error removing link:', error);
    } finally {
      setRemovingId(null);
    }
  };

  /* ─────────────────────── Render ─────────────────────── */

  return (
    <div style={{ marginBottom: 4 }}>
      {/* Existing links */}
      {loading ? (
        <div style={{
          padding: '14px 12px',
          background: 'var(--bg-surface-2)',
          border: '1px dashed var(--border-default)',
          borderRadius: 8,
          fontSize: 14, color: 'var(--text-4)',
          marginBottom: 8,
          display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center',
        }}>
          <Spinner size={12} color="var(--accent)" /> Loading SOV links…
        </div>
      ) : displayLinks.length === 0 && !showPicker ? (
        <div style={{
          padding: '14px 12px',
          background: 'var(--bg-surface-2)',
          border: '1px dashed var(--border-default)',
          borderRadius: 8,
          fontSize: 14, color: 'var(--text-4)',
          marginBottom: 4,
        }}>No SOV links yet</div>
      ) : (
        displayLinks.map(alloc => {
          const contractAmt = parseFloat(alloc.sov_contract_amount) || 0;
          const amount = parseFloat(alloc.allocation_amount) || 0;
          const pct = contractAmt > 0 ? Math.round((amount / contractAmt) * 100) : 0;
          const isPercent = alloc.allocation_method === 'Percentage';
          const valueText = isPercent
            ? `${pct}%`
            : `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
          const isRemoving = removingId === alloc.id;
          const otherRemoving = removingId && !isRemoving;
          return (
            <div key={alloc.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 8,
              marginBottom: 8,
              opacity: otherRemoving ? 0.55 : 1,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                  <span className="mono" style={{ fontSize: 13, color: 'var(--accent-text)', fontWeight: 500 }}>
                    {alloc.sov_code || ''}
                  </span>
                  <span style={{
                    fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{alloc.sov_line_description}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="mono" style={{ fontSize: 13.5, color: 'var(--text-1)', fontWeight: 500, letterSpacing: '-0.02em' }}>
                    {valueText}
                  </span>
                  {contractAmt > 0 && (
                    <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-4)' }}>
                      = ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} of ${contractAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveLink(alloc)}
                disabled={isRemoving || otherRemoving}
                aria-label="Remove SOV link"
                style={{
                  width: 26, height: 26, borderRadius: 6,
                  background: 'transparent', border: 'none', color: 'var(--danger)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  cursor: isRemoving ? 'wait' : (otherRemoving ? 'not-allowed' : 'pointer'),
                }}
                onMouseEnter={(e) => { if (!isRemoving && !otherRemoving) e.currentTarget.style.background = 'var(--danger-soft)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {isRemoving ? <Spinner size={12} color="var(--danger)" /> : <TrashIcon />}
              </button>
            </div>
          );
        })
      )}

      {/* Picker (collapsed: chip; expanded: full panel) */}
      {!showPicker ? (
        <div style={{ marginTop: 4 }}>
          <button
            type="button"
            onClick={() => {
              setShowPicker(true);
              setTimeout(() => searchRef.current?.focus(), 30);
            }}
            disabled={loading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 10px 5px 8px', borderRadius: 7,
              fontSize: 13.5, fontWeight: 500,
              color: 'var(--accent-text)',
              background: 'var(--accent-soft)',
              border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1,
            }}
          >
            <PlusIcon />Link SOV line
          </button>
        </div>
      ) : (
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 10,
          padding: 12,
          boxShadow: 'var(--shadow-sm)',
          marginTop: 4,
        }}>
          {/* SOV Line picker combobox */}
          <label style={{
            display: 'block', fontSize: 12, fontWeight: 500,
            color: 'var(--text-3)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 6,
          }}>SOV Line Item</label>
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)', borderRadius: 8,
            marginBottom: 12, overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px',
              borderBottom: '1px solid var(--border-subtle)',
              color: 'var(--text-3)',
            }}>
              <SearchIcon />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSovLineId(''); }}
                placeholder="Search SOV lines…"
                style={{
                  flex: 1, minWidth: 0,
                  background: 'transparent', border: 'none', outline: 'none',
                  fontSize: 14.5, color: 'var(--text-1)',
                }}
              />
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {filteredSovLines.length === 0 ? (
                <div style={{ padding: '12px 14px', fontSize: 13.5, color: 'var(--text-4)', textAlign: 'center' }}>
                  No matching SOV lines
                </div>
              ) : (
                filteredSovLines.map((line, i) => {
                  const contractAmt = parseFloat(line.contract_amount) || 0;
                  const totalAllocated = sovAllocatedMap[line.id] || 0;
                  const remaining = contractAmt - totalAllocated;
                  // 2-decimal remaining %, computed straight from the dollars
                  // (not rounded to a whole number) so sub-1% slivers like a
                  // $0.25 leftover on a $75k line aren't hidden as a flat "0%".
                  const pctRemNum = contractAmt > 0 ? Math.max(0, (remaining / contractAmt) * 100) : 100;
                  const pctRem = pctRemNum.toFixed(2);
                  const isFull = remaining <= 0.01 && contractAmt > 0;
                  const isSel = String(line.id) === String(sovLineId);
                  return (
                    <div
                      key={line.id}
                      onClick={() => {
                        if (isFull) return;
                        setSovLineId(String(line.id));
                        setSearchQuery(`${line.sov_code || ''} ${line.description || ''}`.trim());
                        // Auto-clamp value to remaining capacity
                        const lineContractAmt = parseFloat(line.contract_amount) || 0;
                        const lineTotalAllocated = sovAllocatedMap[line.id] || 0;
                        const lineRemainingAmt = lineContractAmt - lineTotalAllocated;
                        const lineRemainingPct = lineContractAmt > 0
                          ? Math.round(((lineRemainingAmt) / lineContractAmt) * 100)
                          : 100;
                        if (allocationType === 'Percentage') {
                          const cur = parseFloat(allocationValue || '100');
                          if (cur > lineRemainingPct) setAllocationValue(String(lineRemainingPct));
                        } else if (allocationType === 'Amount') {
                          const cur = parseFloat(allocationValue || '0');
                          if (cur > lineRemainingAmt) setAllocationValue(String(lineRemainingAmt.toFixed(2)));
                        }
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px',
                        borderBottom: i < filteredSovLines.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                        background: isSel ? 'var(--accent-soft)' : 'transparent',
                        opacity: isFull ? 0.55 : 1,
                        cursor: isFull ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={(e) => { if (!isSel && !isFull) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { if (!isSel && !isFull) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                          <span className="mono" style={{
                            fontSize: 13, fontWeight: 500,
                            color: isSel ? 'var(--accent-text)' : 'var(--text-3)',
                          }}>{line.sov_code}</span>
                          <span style={{
                            fontSize: 14, color: 'var(--text-1)',
                            fontWeight: isSel ? 500 : 400,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>{line.description}</span>
                        </div>
                        <div className="mono" style={{ fontSize: 12, color: 'var(--text-4)', marginTop: 2 }}>
                          ${totalAllocated.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} allocated of ${contractAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          {' · '}{pctRem}% remaining
                        </div>
                      </div>
                      <div className="mono" style={{
                        fontSize: 12.5, fontWeight: 500, letterSpacing: '-0.02em',
                        color: isFull ? 'var(--danger)' : pctRemNum < 30 ? 'var(--warning)' : 'var(--text-3)',
                        whiteSpace: 'nowrap',
                      }}>
                        {isFull
                          ? 'Fully allocated'
                          : pctRemNum < 0.005
                            ? `$${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left`
                            : `${pctRem}% left`}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Allocation type + value */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: wouldExceed ? 10 : 12 }}>
            <div>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 500,
                color: 'var(--text-3)', textTransform: 'uppercase',
                letterSpacing: '0.05em', marginBottom: 6,
              }}>Allocation type</label>
              <div style={{
                display: 'inline-flex', alignItems: 'center', height: 34, padding: 2,
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border-default)', borderRadius: 8,
                width: '100%',
              }}>
                {['Percentage', 'Amount'].map(opt => {
                  const active = allocationType === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        setAllocationType(opt);
                        setAllocationValue(opt === 'Percentage' ? '100' : '');
                      }}
                      style={{
                        flex: 1, height: 28, borderRadius: 6,
                        border: 'none',
                        fontSize: 13.5, fontWeight: 500,
                        color: active ? 'var(--text-1)' : 'var(--text-3)',
                        background: active ? 'var(--bg-surface)' : 'transparent',
                        boxShadow: active ? 'var(--shadow-xs)' : 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {opt === 'Percentage' ? 'Percentage' : 'Dollar Amount'}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 500,
                color: 'var(--text-3)', textTransform: 'uppercase',
                letterSpacing: '0.05em', marginBottom: 6,
              }}>Value</label>
              <div style={{
                display: 'flex', alignItems: 'center', height: 34,
                background: 'var(--bg-surface)',
                border: wouldExceed ? '1px solid var(--warning)' : '1px solid var(--border-default)',
                borderRadius: 8, padding: '0 10px',
                boxShadow: wouldExceed ? '0 0 0 3px color-mix(in oklch, var(--warning) 18%, transparent)' : 'none',
                transition: 'box-shadow .12s, border-color .12s',
              }}>
                <input
                  type="number"
                  value={allocationValue}
                  onChange={(e) => setAllocationValue(e.target.value)}
                  placeholder={allocationType === 'Percentage' ? '0' : '0.00'}
                  min="0"
                  step={allocationType === 'Percentage' ? '1' : '0.01'}
                  style={{
                    flex: 1, minWidth: 0, width: '100%',
                    background: 'transparent', border: 'none', outline: 'none',
                    fontFamily: 'var(--font-mono)', fontSize: 14.5, color: 'var(--text-1)',
                    letterSpacing: '-0.02em',
                  }}
                />
                <span style={{
                  color: 'var(--text-3)', fontSize: 14.5, fontFamily: 'var(--font-mono)', marginLeft: 4,
                }}>{allocationType === 'Percentage' ? '%' : '$'}</span>
              </div>
            </div>
          </div>

          {/* Over-allocation warning */}
          {wouldExceed && (
            <div style={{
              position: 'relative',
              padding: '10px 12px 10px 16px',
              background: 'var(--warning-soft)',
              borderRadius: 8,
              marginBottom: 12,
              boxShadow: 'inset 3px 0 0 var(--warning)',
              display: 'flex', alignItems: 'flex-start', gap: 8,
              color: 'var(--warning)',
            }}>
              <AlertTriangleIcon />
              <div style={{ flex: 1, fontSize: 13.5, lineHeight: 1.45 }}>
                <span style={{ fontWeight: 600 }}>
                  This would exceed line total by ${exceedDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({exceedPct.toFixed(1)}%).
                </span>
                <span style={{ display: 'block', marginTop: 2, opacity: 0.85 }}>
                  {selectedSovLine?.sov_code} has ${selectedRemainingAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({Math.round(selectedRemainingPct)}%) remaining. Reduce allocation to fit.
                </span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              onClick={() => { resetForm(); setShowPicker(false); }}
              disabled={addingLink}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '0 14px', height: 32, borderRadius: 8,
                fontSize: 14, fontWeight: 500, color: 'var(--text-2)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-default)',
                cursor: addingLink ? 'not-allowed' : 'pointer',
                opacity: addingLink ? 0.45 : 1,
              }}
            >Cancel</button>
            <button
              type="button"
              onClick={handleAddLink}
              disabled={!sovLineId || !allocationValue || wouldExceed || addingLink}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '0 16px', height: 32, borderRadius: 8,
                fontSize: 14, fontWeight: 500, color: '#fff',
                background: 'var(--accent)',
                border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
                boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
                cursor: addingLink ? 'wait' : ((!sovLineId || !allocationValue || wouldExceed) ? 'not-allowed' : 'pointer'),
                opacity: (!sovLineId || !allocationValue || wouldExceed) ? 0.45 : 1,
              }}
            >
              {addingLink ? <><Spinner size={12} /> Adding…</> : 'Add Link'}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default TaskSOVLinks;
